import { Flex, rem } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { BankProducts } from '@modules/banks/presentation/v2/features/BankProducts';
import {
  Box, Button, Text
} from '@huspy/briks-web';
import {
  useContext, useEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { BankProduct } from '@modules/banks/api/types';
import { InterbankRate } from '@modules/banks/presentation/v2/pages/BankProducts/features/InterbankRate';
import { useForm } from '@mantine/form';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { CITIZEN_STATE } from '@modules/banks/presentation/v1/features/BankProducts/enums';
import { ProposalCreationBankProductsRoute } from '@modules/proposals/presentation/routes';
import useSelectProposalBankProducts from '@modules/proposals/api/mutation/useSelectProposalBankProducts';
import CreditsCharge from '@components/CreditsCharge';
import { SelectedBankProductsList } from '@modules/proposals/presentation/v2/pages/ProposalCreation/features/SelectedBankProductsList';
import { VStack } from '@huspy/styled-system/jsx';
import useCredits from '@modules/core/hooks/useCredits';
import { CREDITS_CHARGE_ACTION_TYPES } from '@modules/core/api/const';
import { ProposalDomain } from '@modules/proposals/domain/proposal';
import { rootContainer } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/BankProducts/styles.css';
import { ProposalCreationContext } from '@modules/proposals/presentation/v2/pages/ProposalCreation/context';
import { useProposalCreationStore } from '@modules/proposals/presentation/v2/pages/ProposalCreation/store';

const MAX_PRODUCTS_SELECTION = 3;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProposalBankProducts = () => {
  const { id } = ProposalCreationBankProductsRoute.useParams();
  const { data, isLoading } = useGetProposalById(id);
  const form = useForm<{ bankProducts: BankProduct[]; }>({ initialValues: { bankProducts: [] } });
  const { t } = useTranslation();
  const { unlockNextStateAndNavigate } = useContext(ProposalCreationContext);
  const ref = useRef<HTMLFormElement>(null);

  const { bankProducts, setBankProducts } = useProposalCreationStore();

  const { callCreditsAction } = useCredits();

  const {
    mutateAsync: mutateBankProductsAsync,
    isPending: isPendingBankProducts,
  } = useSelectProposalBankProducts();

  const selectedBankProducts = form.values.bankProducts;

  const handleOnChange = (bankProducts: BankProduct[]) => {
    form.setFieldValue('bankProducts', bankProducts);
    setBankProducts(bankProducts);
  };

  const mutateBankProducts = async () => {
    await mutateBankProductsAsync({
      proposalExternalId: id,
      bankProductsIds: bankProducts.map((bankProduct) => bankProduct.external_id),
    });

    unlockNextStateAndNavigate();
  };

  const handleOnSubmit = async () => {
    const callback = (data && ProposalDomain.isGenerated(data))
      ? (a: string, b: CallableFunction, _: CallableFunction) => b()
      : callCreditsAction;
    await callback(CREDITS_CHARGE_ACTION_TYPES.proposalGenerate, async () => {
      await mutateBankProducts();
    }, (error: Error) => {
      trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_GENERATION_FAILED, {
        useContext: true,
        blocked: 1,
        proposal_id: id,
        error_message: error.message,
      });
    });
  };

  useEffect(() => {
    if (data) {
      form.setInitialValues({ bankProducts: data.bank_offers as unknown as BankProduct[] ?? [] });
      form.setValues({ bankProducts: data.bank_offers as unknown as BankProduct[] ?? [] });
      setBankProducts(data.bank_offers as unknown as BankProduct[] ?? []);

      trackAmplitudeEvent(USER_EVENTS.PROPOSAL.BANK_PRODUCTS_VIEWED, {
        useContext: true,
        addToContext: true,
        client_id: data.client_external_id,
        proposal_id: data.external_id,
        bank_products: data.bank_offers?.map((bankProducts) => bankProducts.external_id) ?? [],
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    form.setFieldValue('bankProducts', bankProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankProducts]);

  const isSubmitDisabled = selectedBankProducts.length === 0 || isPendingBankProducts || !data;

  return (
    <>
      {
        createPortal(
          <VStack w='100%' mt='10' h='100%' gap='3'>
            <SelectedBankProductsList />
            <Button
              disabled={ isSubmitDisabled }
              loading={ isPendingBankProducts }
              variant={ isPendingBankProducts ? 'outline' : 'primary' }
              w='100%'
              onClick={ handleOnSubmit }
              data-testid='proposals.bank-products.save'
              data-test='generate-proposal'
            >
              <Flex gap={ 6 } align='center'>
                <Text>{(data && ProposalDomain.isGenerated(data)) ? 'Save' : 'Generate proposal'}</Text>
                { !isLoading && data && !ProposalDomain.isGenerated(data)
                  && (
                    <>
                      <Text size='xs' color='text-secondary'>|</Text>
                      <CreditsCharge
                        isDisabled={ isSubmitDisabled }
                        showLeftLine={ false }
                        action_type={ CREDITS_CHARGE_ACTION_TYPES.proposalGenerate }
                      />
                    </>
                  )}
              </Flex>
            </Button>
          </VStack>,
          document.querySelector('#asideContent')!
        )
      }
      <Flex direction='column'>
        <Flex justify='space-between' align='flex-start'>
          <Flex direction='column'>
            <Text size='3xl' weight='medium' color='text-primary'>{t('proposals.proposalsBankProducts.title')}</Text>
            <Text size='md' weight='regular' color='text-secondary'>{t('proposals.proposalsBankProducts.subtitle')}</Text>
          </Flex>

          <Box>
            <InterbankRate />
          </Box>
        </Flex>

        <Flex flex={ 1 } direction='row' pt={ rem(40) }>
          <Box width='100%' pb='30px'>
            { !isLoading && data
            && (
              <BankProducts
                handleOnChange={ handleOnChange }
                financedFeeAmount={ data?.calculated_fees?.total_fee_financed_amount }
                maxProductsSelection={ MAX_PRODUCTS_SELECTION }
                tableClassName={ rootContainer }
                selectedBankProducts={ selectedBankProducts }
                isSimplifiedView
                initialValues={ {
                  loan_amount: [data?.loan_amount ?? 0],
                  mortgage_contract_months: [data?.mortgage_length ?? 0],
                  status_type: ['1'],
                  ...(data?.employment_status ? { type_of_employment: [data.employment_status] } : {}),
                  ...(data?.residence_status ? {
                    citizen_state: data.residence_status === CITIZEN_STATE.uaeNational.value ? [
                      data.residence_status,
                      CITIZEN_STATE.uaeResident.value
                    ] : [data.residence_status],
                  } : {}),
                  ...(data?.transaction_type ? { type_of_transaction: [data.transaction_type] } : {}),
                  ...(data?.mortgage_type
                    ? {
                      type_of_mortgage:
                          data.mortgage_type === 'BEST_POSSIBLE_RATE'
                            ? ['ISLAMIC', 'CONVENTIONAL']
                            : [data.mortgage_type],
                    }
                    : {}),
                } }
              />
            )}
          </Box>
        </Flex>
      </Flex>

      <form onSubmit={ form.onSubmit(async () => handleOnSubmit()) } ref={ ref } />
    </>
  );
};
